/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { reduxForm } from 'global/lib/reduxForm';

import { snakeCase } from 'lodash';

import { Link } from 'react-router';

import withMessages, { FormattedHTMLMessage } from 'component-utilities/intl';
import { PrimaryButton, SecondaryButton } from 'v-c/Buttons/Components';

import TooltipGrayIcon from 'view-components/components/Icons/TooltipGrayIcon';
import EyeGrayIcon from 'view-components/components/Icons/EyeGrayIcon';
import EyeGreenIcon from 'view-components/components/Icons/EyeGreenIcon';
import Tooltip from 'v-c/Tooltip/Tooltip';
import { FormErrorText } from 'v-c/Forms';
import {
  TextLabel,
  InputField,
} from 'view-components/components/Widgets/Components';
import LoadingIndicator from 'view-components/components/LoadingIndicator';

import {
  getCredentials,
  changeCredentialValue,
  updateCredentials,
} from '../../../global/state_managers/ADSyncStateManager';
import { jsUi } from '../../../global/lib/jsUi';

import style from './AppCredentials.scss';
import ADsyncStyle from '../ADSync/ADSync.scss';

const mapStateToProps = ({ adSync, domainInfo }: any) => ({
  accountId: domainInfo.accountId,
  credentials: adSync.credentialData.credentials,
  credentialsLoading: adSync.credentialsLoading,
});

const mapActionsToProps = { getCredentials, changeCredentialValue, updateCredentials };

// Azure AD Client ID and Tenant ID are GUIDs (8-4-4-4-12 hex). We accept the
// GUID with OR without dashes and normalize to the canonical dashed form on
// blur. Validation surfaces an inline error for anything that isn't a GUID, but
// does NOT block Update/Next -- it's a non-blocking warning so an unusual-but-
// valid value is never rejected client-side (SNAR-9852).
const GUID_FIELDS = ['clientId', 'tenantId'];
// 32 hex chars, optionally grouped 8-4-4-4-12 by dashes.
const GUID_DASHED_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GUID_BARE_REGEX = /^[0-9a-f]{32}$/i;

function isGuid(value: string) {
  return GUID_DASHED_REGEX.test(value) || GUID_BARE_REGEX.test(value);
}

// Reformat a valid GUID (dashed or bare) to the canonical 8-4-4-4-12 form.
// Returns the input unchanged if it isn't a recognizable GUID.
function normalizeGuid(value: string) {
  if (!value || !isGuid(value)) {
    return value;
  }

  const hex = value.replace(/-/g, '');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

function validateCredentials(values: any) {
  const errors: { clientId?: string, tenantId?: string } = {};

  GUID_FIELDS.forEach((field) => {
    const value = values[field];
    // Only flag a non-empty value that isn't a GUID; empty fields aren't
    // "invalid format" (nothing has been entered yet).
    if (value && !isGuid(value)) {
      errors[field] = 'app.ad_sync.app_credentials.invalid_guid';
    }
  });

  return errors;
}

type Props = {
  getCredentials: Function,
  getMessage: Function,
  credentials: {},
  credentialsLoading: Boolean,
  credentialData: {},
  changeCredentialValue: Function,
  dirty: Boolean,
  fields: any | { 
    clientId: {
      value: string
    },
    clientSecret: {
      value: string
    },
    tenantId: {
      value: string
    }
  },
  values: any | {},
  updateCredentials: Function,
  initializeForm: Function,
  accountId: string,
}

type State = {
  showSecret: boolean,
}

@withMessages
@connect(
  mapStateToProps,
  mapActionsToProps,
)
@reduxForm(
  {
  form: 'appCredentials',
  fields: ['clientId', 'clientSecret', 'tenantId'],
  // No enableReinitialize: the form is seeded once in componentDidUpdate when
  // credentials finish loading. Reinitializing on every store change caused the
  // form to reset mid-typing and wipe fields, because values are now written
  // back to the adSync store on blur (SNAR-9852).
  validate: validateCredentials,
  },
)
export default class AppCredentials extends Component<Props, State> {
  static propTypes = {
    credentials: PropTypes.object,
    credentialsLoading: PropTypes.bool,
    credentialData: PropTypes.object,
    getMessage: PropTypes.func.isRequired,
    getCredentials: PropTypes.func.isRequired,
    changeCredentialValue: PropTypes.func.isRequired,
    dirty: PropTypes.bool.isRequired,
    fields: PropTypes.object.isRequired,
    values: PropTypes.object.isRequired,
    updateCredentials: PropTypes.func.isRequired,
    initializeForm: PropTypes.func.isRequired,
    accountId: PropTypes.string.isRequired,
  };

  static defaultProps = {
    manageButtonText: 'button.manage',
    credentials: null,
    credentialData: null,
    credentialsLoading: null,
  };

  seeded = false;

  constructor(props: Props) {
    super(props);

    // showSecret toggles the masking on the "Key" (clientSecret) field.
    this.state = { showSecret: false };

    props.getCredentials();
  }

  componentDidUpdate(prevProps: Props) {
    // Seed the form once, when the server credentials finish loading. (We dropped
    // enableReinitialize because it reset the form on every store write and wiped
    // fields mid-typing.) clientSecret always starts blank for security.
    const justLoaded = prevProps.credentialsLoading && !this.props.credentialsLoading;

    if (justLoaded && !this.seeded) {
      this.seeded = true;
      this.props.initializeForm({
        ...this.props.credentials,
        clientSecret: '',
      });
    }
  }

  onCredentialChange = (property: any) => ({ target }: any) => {
    this.props.changeCredentialValue(property, target.value);
  };

  // Commit a field's current value to the adSync store on blur. The wizard's
  // "Next" reads credentials from that store slice to build its createSync /
  // updateCredentials payload, so values must land there -- but only on blur,
  // not per keystroke, so typing no longer races a form reset (SNAR-9852).
  //
  // For the GUID fields (Client ID / Tenant ID) we also normalize to the
  // canonical dashed form on blur, so the user can paste a GUID with or without
  // dashes and the stored/submitted value is always 8-4-4-4-12.
  onFieldBlur = (variableName: string) => () => {
    this.props.fields[variableName].onBlur();

    const rawValue = this.props.values[variableName] || '';
    const value = GUID_FIELDS.includes(variableName)
      ? normalizeGuid(rawValue)
      : rawValue;

    // Reflect the normalized value back into the visible form field.
    if (value !== rawValue) {
      this.props.fields[variableName].onChange(value);
    }

    this.props.changeCredentialValue(variableName, value);
  };

  get inputs() {
    const { getMessage } = this.props;
    const placeholderText = 'XXXXXXXX–XXXX–XXXX–XXXX–XXXXXXXX';

    const { showSecret } = this.state;

    return [
      {
        placeholder: placeholderText,
        type: 'client_id',
        variableName: 'clientId',
        isSecret: false,
      },
      {
        placeholder: placeholderText,
        type: 'client_secret',
        variableName: 'clientSecret',
        isSecret: true,
      },
      {
        placeholder: placeholderText,
        type: 'tenant_id',
        variableName: 'tenantId',
        isSecret: false,
      },
    ].map(({ placeholder = '', type, variableName, isSecret }) => (
      <div key={variableName}>
        <TextLabel className={style['input-label']} required>
          {getMessage(`app.ad_sync.app_credentials.${type}`)}
        </TextLabel>
        <Tooltip
          position="right"
          type="single"
          content={getMessage(`app.ad_sync.app_credentials.${type}_tooltip`)}
        >
          <TooltipGrayIcon />
        </Tooltip>
        <div className={style['input-row']}>
          <InputField
            {...{
              placeholder,
              // Mask the "Key" (clientSecret) like a password; reveal it with the
              // toggle below. Other fields are plain text (SNAR-9852).
              type: isSecret && !showSecret ? 'password' : 'text',
              'data-field': snakeCase(variableName),
              value: this.props.values[variableName] || '',
              // onChange keeps local form state responsive; the value is committed
              // to the adSync store onBlur (see onFieldBlur) so typing doesn't race
              // a form reset.
              onChange: this.props.fields[variableName].onChange,
              onBlur: this.onFieldBlur(variableName),
            }}
          />
          {isSecret && (
            <button
              type="button"
              className={style['secret-toggle']}
              data-action="toggle-secret"
              aria-label={getMessage(
                showSecret
                  ? 'app.ad_sync.app_credentials.hide_key'
                  : 'app.ad_sync.app_credentials.show_key',
              )}
              onClick={() => this.setState(prev => ({ showSecret: !prev.showSecret }))}
            >
              {showSecret ? <EyeGreenIcon /> : <EyeGrayIcon />}
            </button>
          )}
        </div>
        {this.props.fields[variableName].error && (
          <div
            className={style['field-error']}
            data-error={snakeCase(variableName)}
          >
            <FormErrorText
              field={`${snakeCase(variableName)}-error`}
              error={this.props.fields[variableName].error}
            />
          </div>
        )}
      </div>
    ));
  }

  updateHandler() {
    const { clientId, clientSecret, tenantId } = this.props.fields;

    this.props.updateCredentials({
      remoteDirectoryData: {
        ottoAccountId: this.props.accountId,
        credentials: {
          clientId: clientId.value,
          clientSecret: clientSecret.value,
          tenantId: tenantId.value,
        },
      },
    });
  }

  render() {
    const inputs = this.inputs;
    const { credentialsLoading, getMessage, dirty } = this.props;

    const getCredentialMessage = (text: string) =>
      getMessage(`app.ad_sync.app_credentials.${text}`);

    return (
      <div className={style['app-credentials']}>
        {credentialsLoading && (
          <LoadingIndicator
            orientation="middle"
            text={getMessage('app.general.loading')}
            isLoading
          />
        )}
        {!credentialsLoading && (
          <div>
            <TextLabel
              className={`${style['info-text']} ${style['hint-header']}`}
            >
              <FormattedHTMLMessage id="app.ad_sync.app_credentials.hint_header" />
            </TextLabel>
            {inputs}
          </div>
        )}
        {(!this.props.credentialData && !this.props.credentialsLoading) && (
          <div className={ADsyncStyle['action-buttons']}>
            <PrimaryButton
              disabled={!dirty}
              onClick={() => {
                this.updateHandler();
              }}
              extraClassName={ADsyncStyle.update}
              data-action="update"
            >
              {getMessage('app.button.update')}
            </PrimaryButton>
            <Link data-action="cancel" to={jsUi.settingsAdSyncIndex.path()}>
              <SecondaryButton>
                {getMessage('app.button.cancel')}
              </SecondaryButton>
            </Link>
          </div>
        )}
      </div>
    );
  }
}
